import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { useThemeContext } from '../../../theme/ThemeContext'

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
)

/**
 * ClearanceFunnelChart — mixed bar+line chart of forecasting curves by payment position.
 * @param {Array} data - clearanceByPosition items: { position, total, cleared, clearRate }
 *   clearRate is a 0-1 fraction (or null when total is 0); rendered here as a %.
 * @param {Array} retentionData - retentionByPosition items: { position, cohortEligible, retained, retentionRate }
 *   retentionRate is a 0-1 fraction (or null when cohortEligible is 0); rendered as a whole-percent
 *   "Client Retention" line overlaid on the clearance bars. Optional — omitted/empty renders no
 *   extra series and never affects the existing clearance chart.
 */
const ClearanceFunnelChart = ({ data = [], retentionData = [] }) => {
  const { mode } = useThemeContext()

  const tickColor = mode === 'dark' ? '#EDEDED' : '#18181B'
  const gridColor = mode === 'dark' ? '#3F3F46' : '#E4E4E7'

  const hasRetention = Array.isArray(retentionData) && retentionData.length > 0

  const clearanceByPosition = new Map(data.map(item => [item.position, item]))
  const retentionByPosition = new Map(
    hasRetention ? retentionData.map(item => [item.position, item]) : []
  )

  // Positions may not perfectly line up between the two series — key by position and
  // union them so each series only contributes values for positions it actually has.
  const positions = Array.from(
    new Set([...clearanceByPosition.keys(), ...retentionByPosition.keys()])
  ).sort((a, b) => a - b)

  const labels = positions.map(position => `Payment ${position}`)

  const clearValues = positions.map(position => {
    const item = clearanceByPosition.get(position)
    if (!item) return null
    return item.clearRate != null ? Math.round(item.clearRate * 1000) / 10 : 0
  })

  const retentionValues = positions.map(position => {
    const item = retentionByPosition.get(position)
    if (!item || item.retentionRate == null) return null
    return Math.round(item.retentionRate * 100)
  })

  const datasets = [{
    type: 'bar',
    label: 'Clear Rate',
    data: clearValues,
    backgroundColor: 'rgba(21,112,239,0.5)',
    borderColor: '#1570EF',
    borderWidth: 1,
    borderRadius: 4,
    maxBarThickness: 48,
  }]

  if (hasRetention) {
    datasets.push({
      type: 'line',
      label: 'Client Retention',
      data: retentionValues,
      borderColor: '#F79009',
      backgroundColor: '#F79009',
      pointBackgroundColor: '#F79009',
      pointBorderColor: '#fff',
      pointBorderWidth: 1,
      pointRadius: 3,
      pointHoverRadius: 5,
      borderWidth: 2,
      tension: 0.3,
      fill: false,
      spanGaps: true,
    })
  }

  const chartData = { labels, datasets }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: hasRetention,
        labels: {
          color: tickColor,
          boxWidth: 12,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 14 },
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => {
            if (context.raw == null) return ''
            return context.dataset.type === 'line'
              ? `${context.raw}% retained`
              : `${context.raw}% cleared`
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          padding: 10,
          color: tickColor,
        },
        border: {
          color: 'transparent',
        }
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          padding: 10,
          color: tickColor,
          callback: (value) => `${value}%`
        },
        border: {
          dash: [2],
          color: 'transparent',
        },
        grid: {
          color: gridColor,
        }
      }
    }
  }

  return (
    <div className="border border-divider rounded-2xl bg-surface shadow-subtle dark:shadow-none overflow-hidden">
      <div className="p-5 border-b border-divider">
        <h3 className="font-semibold text-[14px] text-text-primary">Clearance Funnel</h3>
        <p className="text-[13px] text-text-secondary mt-0.5">
          % of payments cleared by payment position
        </p>
      </div>

      <div className="p-5">
        {data.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center text-text-secondary">
            No data available
          </div>
        ) : (
          <div className="h-[280px]">
            <Bar key={mode} data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ClearanceFunnelChart
